/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Box,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MemoryIcon from '@mui/icons-material/Memory';
import SensorsIcon from '@mui/icons-material/Sensors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { fetchTarjetaById } from '../../../actions/tarjeta';
import DialogCustom from '../../../components/dialog';
import InfoTarjeta from '../../tarjeta/info-tarjeta';
import InfoCard from '../../../components/info-card';

const AccordionElemento = ({ data }) => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [tarjetaActual, setTarjetaActual] = useState(null);

  const handleShowTarjetaInfo = async (tarjeta) => {
    try {
      // Si la tarjeta ya tiene toda la información, usarla directamente
      if (tarjeta.sensores && Array.isArray(tarjeta.sensores) && tarjeta.graficas !== undefined) {
        setTarjetaActual(tarjeta);
        setOpenDialog(true);
        return;
      }
      
      // Si no, hacer fetch completo
      const result = await dispatch(fetchTarjetaById(tarjeta._id));
      if (result.payload) {
        setTarjetaActual(result.payload);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('Error al cargar tarjeta:', error);
      // Mostrar la tarjeta con la información que tenemos
      setTarjetaActual(tarjeta);
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTarjetaActual(null);
  };

  if (!data || !data.tarjetas || data.tarjetas.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <InfoCard
          title="Sin tarjetas configuradas"
          subtitle="No hay tarjetas asociadas a este elemento"
          icon={<MemoryIcon />}
          items={[]}
          chips={[{ label: 'Sin configurar', color: 'default', variant: 'outlined' }]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <MuiAccordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          sx={{ 
            backgroundColor: '#1F2A38', 
            color: 'white',
            '&:hover': {
              backgroundColor: '#303C54',
            },
            '& .MuiTypography-root': {
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoryIcon />
            <Typography variant="h6">
              Tarjetas ({data.tarjetas.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {data.tarjetas.map((tarjeta) => {
              // Preparar chips de estado
              const statusChips = [];
              if (tarjeta.estatusDispositivo) {
                statusChips.push({ label: 'Activo', color: 'success' });
              } else {
                statusChips.push({ label: 'Inactivo', color: 'error' });
              }
              
              if (tarjeta.alwaysOn) {
                statusChips.push({ label: 'Crítico', color: 'warning' });
              }

              return (
                <Grid item xs={12} md={6} lg={4} key={tarjeta._id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" component="h3" noWrap>
                          {tarjeta.nombre}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Sensores:</strong> {tarjeta.sensores?.length || 0}
                        </Typography>
                        
                        {tarjeta.sensores && tarjeta.sensores.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <SensorsIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {tarjeta.sensores.slice(0, 2).map(s => s.nombre).join(', ')}
                              {tarjeta.sensores.length > 2 && ` +${tarjeta.sensores.length - 2} más`}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {statusChips.map((chip, index) => (
                          <Chip
                            key={index}
                            label={chip.label}
                            color={chip.color}
                            size="small"
                            variant="filled"
                          />
                        ))}
                      </Box>
                    </CardContent>
                    
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<InfoOutlinedIcon />}
                        onClick={() => handleShowTarjetaInfo(tarjeta)}
                        variant="outlined"
                        fullWidth
                      >
                        Ver Detalles
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </MuiAccordion>

      {/* Modal de detalles de tarjeta */}
      <DialogCustom
        open={openDialog}
        onClose={handleCloseDialog}
        title={tarjetaActual?.data?.nombre || tarjetaActual?.nombre || 'Detalle de Tarjeta'}
        maxWidth="lg"
      >
        {tarjetaActual && (
          <InfoTarjeta tarjeta={tarjetaActual} />
        )}
      </DialogCustom>
    </Box>
  );
};
AccordionElemento.propTypes = {
  data: PropTypes.shape({
    tarjetas: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
      estatusDispositivo: PropTypes.bool,
      alwaysOn: PropTypes.bool,
      sensores: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.string,
        nombre: PropTypes.string
      }))
    }))
  }),
};

AccordionElemento.defaultProps = {
  data: null,
};

export default AccordionElemento;
